import { FaExclamationTriangle } from 'react-icons/fa';
import Callout from '@/components/UI/Callout';
import ConfirmationDialog from '@/components/UI/ConfirmationDialog';
import type { AlphaAtlasReportRow } from './AlphaAtlasReportState';
import { describeCombination, formatHorizon } from './alphaAtlasFormat';

interface AlphaAtlasRevealDialogProps {
    target: AlphaAtlasReportRow | null;
    isPending: boolean;
    error: string | null;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * Deliberate confirmation for the one irreversible action on the page: revealing a hypothesis's
 * full history permanently records that its verification evidence was exposed.
 */
const AlphaAtlasRevealDialog = ({ target, isPending, error, onConfirm, onCancel }: AlphaAtlasRevealDialogProps) => (
    <ConfirmationDialog
        cancelText="Keep controlled"
        confirmText={isPending ? 'Recording exposure…' : 'Reveal and record exposure'}
        confirmVariant="warning"
        icon={<FaExclamationTriangle className="text-amber-400" />}
        isConfirmLoading={isPending}
        isOpen={target !== null}
        message={
            target && (
                <div className="space-y-3 text-sm">
                    <p>
                        You are about to reveal the whole-range and recent-year results for{' '}
                        <strong className="text-white">{describeCombination(target)}</strong> at the{' '}
                        <strong className="text-white">{formatHorizon(target.horizonSeconds)}</strong> horizon.
                    </p>
                    <ul className="list-disc space-y-1 pl-5 text-zinc-400">
                        <li>This is permanent and recorded: who revealed it, when, and for which hypothesis.</li>
                        <li>
                            Those windows include the verification interval, so they stop being independent evidence for this hypothesis.
                        </li>
                        <li>The results stay useful for research, but can no longer be described as untouched historical verification.</li>
                    </ul>
                    {error && (
                        <Callout title="The reveal was not recorded" variant="error">
                            {error}
                        </Callout>
                    )}
                </div>
            )
        }
        title="Reveal full history for this hypothesis?"
        onCancel={onCancel}
        onConfirm={onConfirm}
    />
);

export default AlphaAtlasRevealDialog;
